import { AnyAction } from 'redux';
import createSagaMiddleware from 'redux-saga';
import combineSectionReducers from 'combine-section-reducers';
import {
  GlobalPut,
  integrateReduxDevtoolsExtension,
  NgRefreshStore,
  PS20Store,
} from '@ps20-frontend/shop-integration-redux';
import { App } from '@ps20-frontend/frontend-models';
import { configureStore, Dispatch, Middleware, Tuple, UnknownAction } from '@reduxjs/toolkit';
import { gesamtbeitragReducerForApp } from '@ps20-frontend/reducers';
import { IAppState } from './appState';
import { INITIAL_STATE } from './initialState';
import { rootReducer } from './reducers/rootReducer.reducer';
import { produktReducer } from './reducers/produkte.reducer';
import { rootSaga } from './sagas/rootSaga';
import { notificationsReducer } from './reducers/notifications.reducer';
import { immobilienReducer } from './reducers/immobilien.reducer';
import { zahlungsweiseReducer } from './reducers/zahlungsweise.reducer';
import { transientStateReducer } from './reducers/transient-state.reducer';
import { showSofortschutzHinweistextReducer } from './reducers/showSofortschutzHinweistextReducer';
import { vorvertragsUebersichtReducer } from './reducers/vorvertragsUebersicht.reducer';
import { unfallZaehlstueckReducer } from './reducers/unfallZaehlstueckReducer';
import { vorvertragHinweisReducer } from './reducers/vorvertragHinweis.reducer';
import { standaloneProduktReducer } from './reducers/standaloneProdukt.reducer';
import { fremdVertragReducer } from './reducers/fremdVertrag.reducer';
import { versicherungsNehmerKundennummerReducer } from './reducers/versicherungsNehmerKundennummerReducer';
import { effektiveZaehlstueckeReducer } from './reducers/effektiveZaehlstuecke.reducer';
import { versicherungsnehmerJuristisch } from './reducers/versicherungsnehmerJuristisch';
import { unfallPersonenReducer } from './reducers/unfallPersonen.reducer';
import { versicherungsnehmerReducer } from './reducers/versicherungsnehmer.reducer';
import { vermittlerReducer } from './reducers/vermittler.reducer';
import { weitereVertraegeReducer } from './reducers/weitereVertraege.reducer';
import { kooperationReducer } from './reducers/kooperation.reducer';
import { assekuranztarifReducer } from './reducers/assekuranztarif.reducer';

export const reducers = combineSectionReducers({
  unfallPersonen: unfallPersonenReducer,
  version: (version: number = null) => version,
  showSofortschutzHinweistext: showSofortschutzHinweistextReducer,
  warenkorb: produktReducer,
  unfallZaehlstueck: unfallZaehlstueckReducer,
  notifications: notificationsReducer,
  gesamtbeitrag: gesamtbeitragReducerForApp(App.SHOP_DASHBOARD),
  zahlungsweise: zahlungsweiseReducer,
  immobilien: immobilienReducer,
  transientState: transientStateReducer,
  vorvertraege: vorvertragsUebersichtReducer,
  standaloneProdukte: standaloneProduktReducer,
  vorvertragHinweis: vorvertragHinweisReducer,
  fremdVertraege: fremdVertragReducer,
  kundennummer: versicherungsNehmerKundennummerReducer,
  juristisch: versicherungsnehmerJuristisch,
  effektiveZaehlstuecke: effektiveZaehlstueckeReducer,
  versicherungsnehmer: versicherungsnehmerReducer,
  vermittler: vermittlerReducer,
  weitereVertraege: weitereVertraegeReducer,
  kooperation: (sectionState: string, action: AnyAction, state: IAppState) =>
    kooperationReducer(sectionState, action, state),
  isAssekuranztarif: assekuranztarifReducer,
});

export function storeFactory(initialState: IAppState): PS20Store<IAppState> {
  const ngRefreshStore = new NgRefreshStore<IAppState>(App.SHOP_DASHBOARD);
  const sagaMiddleware = createSagaMiddleware();
  const store = configureStore({
    reducer: rootReducer(reducers),
    devTools: integrateReduxDevtoolsExtension('shop-dashboard'),
    preloadedState: initialState,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware().concat(sagaMiddleware, ngRefreshStore.ngRefreshMiddleware) as unknown as Tuple<ReadonlyArray<Middleware<Record<string, never>, IAppState, Dispatch<UnknownAction>>>>,
  });
  sagaMiddleware.run(rootSaga);
  ngRefreshStore.setDelegate(store);
  return ngRefreshStore;
}

export const localStore: PS20Store<IAppState> = storeFactory(INITIAL_STATE);

GlobalPut.getInstance().globalEventDispatcherFunction = () => {
  return localStore.globalEventDispatcherRef;
};
